import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Title26, Title20, Eyebrow, Title32 } from 'components/Typography'
import { FadeInUpAnimationWrapper } from 'styles/animation'
import CarouselItemModal from 'components/CarouselItemModal'
import MarkdownRenderer from 'components/MarkdownRenderer'
import VideoPlayer from 'components/VideoPlayer'

import ProductCard from './LifestyleProducts/LifestyleProductCard/ProductCard'
import LifestyleProductsCarousel from './LifestyleProducts/LifestyleProductsCarousel'

import {
  Wrapper,
  Container,
  SubstoryContainer,
  StoryContainer,
  StoryBackground,
  StoryTitle,
  StoryDescription,
  StoryImage,
  StoryImageContainer,
  StoryImageTitle,
  ProductContainer,
  ProductCardContainer,
  QuoteContainer,
  QuoteTitle,
  QuoteBody,
  FeaturedProductsContainer,
  FeaturedProductsTitle,
  VideoContainer,
} from './LifestyleSubstory.style'

const LifestyleSubstory = ({
  storyTitle,
  storyDescription,
  image,
  imageTitle,
  product,
  quoteTitle,
  quoteBody,
  featuredProductsTitle,
  featuredProducts,
  hexCodeValue,
  positionFromRightSide,
  villageSlug,
}) => {
  const storyRef = useRef(null)
  const productModalRef = useRef(null)
  const [isIntersecting, setIntersecting] = useState(false)
  const [isDesktopSize, setIsDesktopSize] = useState(true)

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0.3,
  }

  const observerCallback = (entries) => {
    const [entry] = entries
    setIntersecting(entry.isIntersecting)
  }

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, options)

    if (storyRef.current) observer.observe(storyRef.current)

    return () => {
      if (storyRef.current) observer.unobserve(storyRef.current)
    }
  }, [storyRef, options])

  const updateWidth = () => {
    setIsDesktopSize(window.innerWidth > 900)
  }

  useEffect(() => {
    setIsDesktopSize(window.innerWidth > 900)
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [isDesktopSize])

  const renderImageComponent = () => {
    if (image?.portrait) {
      // Image component
      return (
        <FadeInUpAnimationWrapper enabled>
          <StoryImage
            id="storyImage"
            image={image?.portrait?.gatsbyImageData}
            alt={image?.altText || ''}
          />
        </FadeInUpAnimationWrapper>
      )
    }

    if (image?.image) {
      // Product component
      return (
        <ProductCard
          {...image}
          brand={image.productBrandNameOverride || image?.brand?.brandName}
          fullSize
          onclickProductModal={() => {
            if (productModalRef.current) {
              productModalRef.current.openModal({
                ...image,
                villageName: image?.brand?.village?.villageName,
                brand:
                  image.productBrandNameOverride || image?.brand?.brandName,
              })
            }
          }}
        />
      )
    }

    // Video component
    return (
      <VideoContainer>
        <VideoPlayer
          name={image.name}
          controls={false}
          loop
          muted
          video={{
            portrait: image?.videoPortrait,
            landscape: image?.videoLandscape,
            opacity: 0,
          }}
          autoplay
          image={image?.videoPlaceholder?.portrait?.gatsbyImageData}
          videoMode="portrait"
          villageSlug={villageSlug}
        />
      </VideoContainer>
    )
  }

  const storyContent = () => (
    <StoryContainer ref={storyRef}>
      <StoryBackground
        backgroundColor={hexCodeValue}
        isIntersecting={isIntersecting}
      />
      <Title32 as={StoryTitle} isIntersecting={isIntersecting}>
        {storyTitle}
      </Title32>
      <MarkdownRenderer
        isIntersecting={isIntersecting}
        linkColor={hexCodeValue}
        as={StoryDescription}
        html={storyDescription?.childMarkdownRemark?.html}
      />
      <FadeInUpAnimationWrapper enabled>
        <StoryImageContainer isProduct={image?.image}>
          {renderImageComponent()}
          {image?.image ? null : (
            <Title20 as={StoryImageTitle}>{imageTitle}</Title20>
          )}
        </StoryImageContainer>
      </FadeInUpAnimationWrapper>
    </StoryContainer>
  )

  const productContent = () => (
    <ProductContainer
      product={product}
      quoteTitle={quoteTitle}
      quoteBody={quoteBody}
      featuredProducts={featuredProducts}>
      <ProductCardContainer>
        <FadeInUpAnimationWrapper enabled>
          <ProductCard
            {...product}
            brand={product?.brand?.brandName}
            fullSize
            onclickProductModal={() => {
              if (productModalRef.current) {
                productModalRef.current.openModal({
                  ...product,
                  villageName: product?.brand?.village?.villageName,
                  brand:
                    product.productBrandNameOverride ||
                    product?.brand?.brandName,
                })
              }
            }}
            villageSlug={villageSlug}
          />
        </FadeInUpAnimationWrapper>
      </ProductCardContainer>
      {quoteBody && quoteTitle ? (
        <QuoteContainer>
          <Eyebrow as={QuoteTitle}>{quoteTitle}</Eyebrow>
          <MarkdownRenderer
            linkColor={hexCodeValue}
            as={QuoteBody}
            html={quoteBody?.childMarkdownRemark?.html}
          />
        </QuoteContainer>
      ) : null}
    </ProductContainer>
  )

  return (
    <Wrapper>
      <Container>
        <SubstoryContainer>
          {positionFromRightSide && isDesktopSize
            ? [productContent(), storyContent()]
            : [storyContent(), productContent()]}
        </SubstoryContainer>
        {featuredProducts.length > 0 ? (
          <FeaturedProductsContainer>
            <Title26 as={FeaturedProductsTitle}>
              {featuredProductsTitle}
            </Title26>
            <LifestyleProductsCarousel
              products={featuredProducts}
              hexValue={hexCodeValue}
            />
          </FeaturedProductsContainer>
        ) : null}
        <CarouselItemModal ref={productModalRef} />
      </Container>
    </Wrapper>
  )
}

LifestyleSubstory.propTypes = {
  storyTitle: PropTypes.string.isRequired,
  storyDescription: PropTypes.shape({
    childMarkdownRemark: PropTypes.shape({
      html: PropTypes.string,
    }),
  }).isRequired,
  image: PropTypes.oneOfType([PropTypes.object]).isRequired,
  imageTitle: PropTypes.string.isRequired,
  product: PropTypes.oneOfType([PropTypes.object]).isRequired,
  quoteTitle: PropTypes.string,
  quoteBody: PropTypes.shape({
    childMarkdownRemark: PropTypes.shape({
      html: PropTypes.string,
    }),
  }),
  featuredProductsTitle: PropTypes.string,
  featuredProducts: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  hexCodeValue: PropTypes.string.isRequired,
  positionFromRightSide: PropTypes.bool,
  villageSlug: PropTypes.string,
}

LifestyleSubstory.defaultProps = {
  quoteTitle: '',
  quoteBody: {},
  featuredProductsTitle: '',
  featuredProducts: [],
  positionFromRightSide: false,
  villageSlug: '',
}

export default LifestyleSubstory
